import { useState, useEffect, useCallback } from 'react';
import { FileText, MapPin, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { api, fileUrl } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

const FILTERS = ['all', 'Submitted', 'Under Review', 'More Information Needed', 'Approved', 'Approved With Conditions', 'Waitlisted', 'Declined', 'Suspended', 'Inactive'];
const FLAGS = ['Preferred supplier', 'Backup supplier', 'Seasonal supplier', 'Trial supplier'];
const ACTIONS = [
  { status: 'Under Review', label: 'Mark Under Review', cls: 'btn-secondary' },
  { status: 'More Information Needed', label: 'Request More Info', cls: 'btn-secondary' },
  { status: 'Approved', label: 'Approve', cls: 'btn-primary' },
  { status: 'Approved With Conditions', label: 'Approve w/ Conditions', cls: 'btn-primary' },
  { status: 'Waitlisted', label: 'Waitlist', cls: 'btn-secondary' },
  { status: 'Declined', label: 'Decline', cls: 'btn-secondary' },
  { status: 'Suspended', label: 'Suspend', cls: 'btn-secondary' },
  { status: 'Approved', label: 'Reactivate', cls: 'btn-secondary' },
];

const statusColor = (s) => ({ 'Approved': 'bg-forest/10 text-forest', 'Approved With Conditions': 'bg-forest/10 text-forest', 'Submitted': 'bg-gold/15 text-gold-dark', 'Under Review': 'bg-gold/15 text-gold-dark', 'More Information Needed': 'bg-gold/20 text-gold-dark', 'Declined': 'bg-red-50 text-red-600', 'Suspended': 'bg-red-50 text-red-600', 'Waitlisted': 'bg-brown/10 text-brown' }[s] || 'bg-dark/10 text-dark');

export default function AdminApplications() {
  const [filter, setFilter] = useState('all');
  const [apps, setApps] = useState([]);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [flags, setFlags] = useState([]);

  const load = useCallback(() => api.get(`/admin/applications?status=${filter}`).then((r) => setApps(r.data)).catch(() => {}), [filter]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (farmId) => {
    const r = await api.get(`/admin/applications/${farmId}`);
    setDetail(r.data); setNote(''); setAdminNotes(r.data.farm.admin_notes || ''); setFlags(r.data.farm.flags || []);
  };

  const changeStatus = async (status) => {
    await api.post(`/admin/applications/${detail.farm.farm_id}/status`, { status, note });
    toast.success(`Status: ${status}`); setDetail(null); load();
  };
  const saveNotes = async () => { await api.put(`/admin/applications/${detail.farm.farm_id}/notes`, { admin_notes: adminNotes }); toast.success('Internal notes saved'); };
  const toggleFlag = async (f) => {
    const next = flags.includes(f) ? flags.filter((x) => x !== f) : [...flags, f];
    setFlags(next); await api.put(`/admin/applications/${detail.farm.farm_id}/flags`, { flags: next });
  };

  return (
    <div data-testid="admin-applications">
      <div className="flex gap-2 flex-wrap mb-6">
        {FILTERS.map((f) => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-sm font-manrope font-medium ${filter === f ? 'bg-forest text-cream' : 'bg-white text-dark/60 border border-forest/10'}`} data-testid={`filter-${f.replace(/[^a-zA-Z]/g, '-')}`}>{f === 'all' ? 'All' : f}</button>)}
      </div>

      <div className="bg-white rounded-2xl border border-forest/10 overflow-hidden">
        {apps.length === 0 ? <p className="p-12 text-center font-manrope text-dark/50">No applications.</p> : (
          <div className="divide-y divide-forest/10">
            {apps.map((a) => (
              <button key={a.farm_id} onClick={() => openDetail(a.farm_id)} className="w-full text-left p-5 flex flex-wrap items-center gap-4 hover:bg-cream/60 transition-colors" data-testid={`app-row-${a.farm_id}`}>
                <div className="flex-1 min-w-[200px]">
                  <h3 className="font-playfair text-lg font-bold text-dark">{a.name}</h3>
                  <p className="font-manrope text-sm text-dark/60">{a.application?.town || '—'}, {a.application?.state || 'NH'} · {a.contact_name} · {(a.product_categories || []).join(', ') || 'No products yet'}</p>
                </div>
                {a.flags?.length > 0 && <span className="badge-local">{a.flags[0]}</span>}
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusColor(a.status)}`}>{a.status}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-cream max-w-3xl max-h-[92vh] overflow-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-playfair text-2xl">{detail.farm.name} <span className={`ml-2 align-middle px-3 py-1 rounded-full text-xs font-bold ${statusColor(detail.farm.status)}`}>{detail.farm.status}</span></DialogTitle><DialogDescription>Review the application, documents and set a status.</DialogDescription></DialogHeader>
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-3 font-manrope text-sm">
                  <p className="flex items-center gap-2 text-dark/70"><Mail className="w-4 h-4 text-forest" />{detail.farm.email}</p>
                  <p className="flex items-center gap-2 text-dark/70"><Phone className="w-4 h-4 text-forest" />{detail.farm.phone}</p>
                  <p className="flex items-center gap-2 text-dark/70"><MapPin className="w-4 h-4 text-forest" />{detail.farm.application?.address}, {detail.farm.application?.town}, {detail.farm.application?.state} {detail.farm.application?.zip}</p>
                  <p className="text-dark/70">Pricing: {detail.farm.application?.pricing_option}</p>
                </div>

                {(detail.farm.application?.website_social) && <div><p className="font-manrope text-xs text-dark/50 uppercase tracking-wide mb-1">Website / Social</p><p className="font-manrope text-dark">{detail.farm.application.website_social}</p></div>}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div><p className="font-manrope text-xs text-dark/50 uppercase tracking-wide mb-1">Business types</p><div className="flex flex-wrap gap-1.5">{(detail.farm.application?.business_types || []).map((b) => <span key={b} className="badge-local">{b}</span>)}</div></div>
                  <div><p className="font-manrope text-xs text-dark/50 uppercase tracking-wide mb-1">Products</p><div className="flex flex-wrap gap-1.5">{(detail.farm.application?.products_available || []).map((b) => <span key={b} className="badge-organic">{b}</span>)}</div></div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 font-manrope text-sm bg-white rounded-xl p-4">
                  {[['Availability', 'availability'], ['Preferred contact method', 'preferred_contact']].map(([l, k]) => (
                    <div key={k} className="flex justify-between"><span className="text-dark/50">{l}</span><span className="text-dark font-medium">{detail.farm.application?.[k] || '—'}</span></div>
                  ))}
                </div>

                <p className="font-manrope text-xs text-dark/40 italic">Detailed capacity, pricing, pickup preferences, documents, and payout info are collected after approval via the Farm Partner onboarding portal.</p>

                {(detail.farm.application?.compliance || []).length > 0 && <div><p className="font-manrope text-xs text-dark/50 uppercase tracking-wide mb-1">Compliance claimed (unverified)</p><div className="flex flex-wrap gap-1.5">{detail.farm.application.compliance.map((c) => <span key={c} className="badge-local">{c}</span>)}</div></div>}

                <div>
                  <p className="font-manrope text-xs text-dark/50 uppercase tracking-wide mb-2">Documents ({detail.documents.length})</p>
                  {detail.documents.length === 0 ? <p className="font-manrope text-sm text-dark/50">No documents uploaded.</p> : (
                    <div className="space-y-1.5">{detail.documents.map((d) => <a key={d.file_id} href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 bg-white rounded-lg hover:bg-forest/5 font-manrope text-sm text-forest" data-testid={`doc-link-${d.file_id}`}><FileText className="w-4 h-4" />{d.doc_type} — {d.original_filename}</a>)}</div>
                  )}
                </div>

                <div>
                  <p className="font-manrope text-xs text-dark/50 uppercase tracking-wide mb-2">Supplier flags (internal)</p>
                  <div className="flex flex-wrap gap-3">{FLAGS.map((f) => <label key={f} className="flex items-center gap-2 font-manrope text-sm cursor-pointer"><Checkbox checked={flags.includes(f)} onCheckedChange={() => toggleFlag(f)} data-testid={`flag-${f.replace(/[^a-z]/gi, '-')}`} />{f}</label>)}</div>
                </div>

                <div>
                  <p className="font-manrope text-xs text-dark/50 uppercase tracking-wide mb-2">Internal notes (farm cannot see)</p>
                  <Textarea className="bg-white border-forest/20" rows={2} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} data-testid="admin-notes" />
                  <button onClick={saveNotes} className="text-forest text-sm font-medium mt-1 hover:underline" data-testid="save-notes">Save notes</button>
                </div>

                <div className="bg-white rounded-xl p-4">
                  <p className="font-manrope text-xs text-dark/50 uppercase tracking-wide mb-2">Note to farm (sent with decision)</p>
                  <Textarea className="bg-cream border-forest/20 mb-3" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional message included in the status email/notification" data-testid="decision-note" />
                  <div className="flex flex-wrap gap-2">
                    {ACTIONS.map((a) => <button key={a.label} onClick={() => changeStatus(a.status)} className={`${a.cls} py-2 px-4 text-sm`} data-testid={`action-${a.label.replace(/[^a-z]/gi, '-')}`}>{a.label}</button>)}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
